import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { CartItemState } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS, LabelM } from '@sorare/core/src/atoms/typography';
import { HorizontalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { cart } from '@sorare/core/src/lib/glossary';
import {
  ClickRemoveFromCart_Origin,
  ClickSaveForLater_Origin,
} from '@sorare/core/src/protos/events/so5/shared/events';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MarketGrid } from 'components/market/Grid';
import { useCart } from 'hooks/useCart';

import { CartItem } from '../CartItem';
import { ClearButton } from '../ClearButton';
import { EmptyCart } from '../EmptyCart';
import { InLineupCornerBadge } from '../InLineupCornerBadge';
import type { MainCartItems_cartItem } from './__generated__/index.graphql';

const Header = styled(Vertical).attrs({ gap: 1 })`
  align-items: baseline;
  @media ${tabletAndAbove} {
    flex-direction: row;
  }
`;

const Section = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
`;
const Wrapper = styled(MarketGrid)`
  flex-direction: column;
  gap: var(--unit);
  width: 100%;
`;
const CartItemWrapper = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
`;

const PositionedCornerBadge = styled.div`
  position: absolute;
  right: 0;
  top: var(--unit);
`;
const CartItemSkeleton = styled(Skeleton)`
  width: 100%;
  height: 161px;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
`;
const TopLine = styled(SBHorizontal)`
  justify-content: space-between;
  width: 100%;
  @media ${tabletAndAbove} {
    width: auto;
  }
`;
const Disclaimer = styled(Horizontal)`
  flex-grow: 1;

  justify-content: flex-end;
  @media ${tabletAndAbove} {
    width: auto;
  }
`;

type LineupCard = {
  slug: string;
  rarityTyped: Rarity;
  anyPlayer: { slug: string };
};

type CartItemsProps = {
  cartItems: MainCartItems_cartItem[];
  count: number;
  origin: {
    remove: ClickRemoveFromCart_Origin;
    saveForLater: ClickSaveForLater_Origin;
  };
  lineupCards?: LineupCard[];
};
const CartItems = ({
  cartItems,
  count,
  origin,
  lineupCards,
}: CartItemsProps) => {
  const isLineup = (card: LineupCard) =>
    lineupCards?.some(
      lineupCard =>
        lineupCard.slug === card.slug ||
        (lineupCard.rarityTyped === card.rarityTyped &&
          lineupCard.anyPlayer.slug === card.anyPlayer.slug)
    );
  return (
    <Wrapper columns={1}>
      {count - cartItems.length > 0 &&
        Array.from({ length: count - cartItems.length }, (v, i) => i).map(i => (
          <CartItemSkeleton key={i} />
        ))}
      {cartItems.map(cartItem => (
        <CartItemWrapper key={cartItem.id}>
          {isLineup(cartItem.card) && (
            <PositionedCornerBadge>
              <InLineupCornerBadge />
            </PositionedCornerBadge>
          )}
          <CartItem
            withPromoBanner
            cartItem={cartItem}
            card={cartItem.card}
            origin={origin}
          />
        </CartItemWrapper>
      ))}
    </Wrapper>
  );
};

type Props = {
  cartItems: MainCartItems_cartItem[];
  lineupCards?: LineupCard[];
};

export const MainCartItems = ({ cartItems, lineupCards }: Props) => {
  const { formatMessage } = useIntlContext();
  const { cartCount, removedCartCount, removeFromCart } = useCart();

  const availableCartItems = cartItems.filter(
    cartItem => cartItem.state !== CartItemState.REMOVED
  );

  const removedCartItems = cartItems.filter(
    cartItem => cartItem.state === CartItemState.REMOVED
  );

  if (!cartCount && !removedCartCount) return <EmptyCart />;

  return (
    <Vertical>
      <Header>
        <TopLine>
          <HeadlineS as="h2">
            <FormattedMessage
              id="cartItems.title"
              defaultMessage="{total} {total, plural, one {card} other {Cards}}"
              values={{ total: cartItems.length || cartCount }}
            />
          </HeadlineS>
          <ClearButton
            onClear={async () => {
              await removeFromCart.mutate(
                cartItems.map(cartItem => cartItem.id),
                cartItems?.[0].card.sport
              );
            }}
            title={
              <FormattedMessage
                id="mainCartItems.clearCart.title"
                defaultMessage="Clear your cart?"
              />
            }
            message={
              <FormattedMessage
                id="mainCartItems.clearCart.subtitle"
                defaultMessage="Are you sure you want to delete {value, plural, =1 {this card} other {these # cards}} from your cart? This cannot be undone"
                values={{ value: cartItems.length }}
              />
            }
            cta={formatMessage(cart.clearAll)}
          >
            <FormattedMessage
              id="cartItems.clearCart"
              defaultMessage="Clear cart"
            />
          </ClearButton>
        </TopLine>
        <Disclaimer>
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage
              id="cartItems.disclaimer.t"
              defaultMessage="Cards in your cart are not reserved"
            />
          </LabelM>
        </Disclaimer>
      </Header>
      <Section className={classNames({ 'clear-cart': removeFromCart.loading })}>
        <CartItems
          cartItems={availableCartItems}
          count={cartCount}
          origin={{
            remove: ClickRemoveFromCart_Origin.CART_MAIN,
            saveForLater: ClickSaveForLater_Origin.CART_MAIN,
          }}
          lineupCards={lineupCards}
        />
        {!!cartCount && !!removedCartCount && <HorizontalDivider />}
        <CartItems
          cartItems={removedCartItems}
          count={removedCartCount}
          origin={{
            remove: ClickRemoveFromCart_Origin.CART_MAIN_REMOVED,
            saveForLater: ClickSaveForLater_Origin.CART_MAIN_REMOVED,
          }}
        />
      </Section>
    </Vertical>
  );
};

MainCartItems.fragments = {
  cartItem: gql`
    fragment MainCartItems_cartItem on CartItem {
      id
      card {
        slug
        rarityTyped
        anyPlayer {
          slug
        }
        ...CartItem_anyCard
      }
      ...CartItem_cartItem
    }
    ${CartItem.fragments.anyCard}
    ${CartItem.fragments.cartItem}
  ` as TypedDocumentNode<MainCartItems_cartItem>,
};
